<template>
  <article>
    <AreaTopicsDoeBanner />

    <TheNavigation
      :trigger-id="triggerId"
      :navigation="navigation"
      :is-show-mobile-menu="isShowMobileMenu"
      @click-menu-button="isShowMobileMenu = !isShowMobileMenu"
    >
      <template #right-side>
        <TheButton
          class="w-[110px] tablet:w-[180px]"
          :title="t('item-button')"
          :to="`/${$i18n.locale}/marketplace/help/sales-support?IssueType=1`"
        >
          <span class="text-sm">
            {{ t('item-button') }}
          </span>
        </TheButton>
      </template>
    </TheNavigation>

    <TheScrollTrigger :offset="offset" @trigger="handleTrigger('challenges')">
      <AreaTopicsDoeChallenges id="challenges" />
    </TheScrollTrigger>

    <TheScrollTrigger
      :offset="offset"
      @trigger="handleTrigger('why-advantech')"
    >
      <AreaTopicsDoeWhyAdvantech id="why-advantech" />
    </TheScrollTrigger>

    <TheScrollTrigger
      :offset="offset"
      @trigger="handleTrigger('digital-journey')"
    >
      <AreaTopicsDoeDigitalJourney id="digital-journey" />
    </TheScrollTrigger>

    <TheScrollTrigger :offset="offset" @trigger="handleTrigger('use-cases')">
      <AreaTopicsDoeUseCases id="use-cases" />
      <AreaTopicsDoeGetStarted />
    </TheScrollTrigger>

    <NewsletterSection />

    <TheNavigationMenu
      :navigation="navigation"
      :is-show-mobile-menu="isShowMobileMenu"
      @click-item-button="isShowMobileMenu = false"
    />
  </article>
</template>

<script setup lang="ts">
  const { t } = useI18n({ useScope: 'local' });

  const { headerHeight, navigationHeight, provideWebTitle } = useWebsite();
  const { arrived } = useScrollArrivedBottom();

  const triggerId = ref<string>('');

  const isShowMobileMenu = ref<boolean>(false);

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const navigation = computed((): { id: string; name: string }[] => [
    { id: 'challenges', name: t('challenges') },
    { id: 'why-advantech', name: t('why-advantech') },
    { id: 'digital-journey', name: t('digital-journey') },
    { id: 'use-cases', name: t('use-cases') },
  ]);

  const handleTrigger = (id: string): void => {
    if (!arrived.value) {
      triggerId.value = id;
    }
  };

  watch(arrived, (value) => {
    if (value) {
      const id = navigation.value[navigation.value.length - 1]?.id || '';
      triggerId.value = id;
    }
  });

  useSeoMeta({
    title: () => t('seo-title'),
    ogTitle: () => provideWebTitle(t('seo-title')),
    twitterTitle: () => provideWebTitle(t('seo-title')),
    description: () => t('seo-description'),
    ogDescription: () => t('seo-description'),
    twitterDescription: () => t('seo-description'),
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "en-jp": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "en-tw": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "en-kr": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "en-americas": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "en-asia-pacific": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "en-emea": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "ja-jp": {
    "seo-title": "AIを活用したデジタルO&Mとエネルギー管理",
    "seo-description": "Advantech WISEは、現場・設備データを統合し、遠隔運用や複数拠点の可視化、AIによる最適化を実現。エネルギー・ユーティリティ企業の安定運用と効率向上を支援します。",
    "challenges": "O&Mの課題",
    "why-advantech": "Advantech WISEを選ぶ理由",
    "digital-journey": "デジタルO&Mの導入ステージ",
    "use-cases": "ユースケース",
    "item-button": "お問い合わせ"
  },
  "zh-cn": {
    "seo-title": "AI-Powered Digital O&M for Energy Operations",
    "seo-description": "Advantech WISE connects site data for remote operations, multi-site visibility, and AI-driven optimization—helping energy and utilities operate more reliably and efficiently.",
    "challenges": "Challenges",
    "why-advantech": "Why Advantech WISE",
    "digital-journey": "Digital O&M Journey",
    "use-cases": "Use Cases",
    "item-button": "Contact Us"
  },
  "zh-tw": {
    "seo-title": "AI 驅動數位維運與能源管理",
    "seo-description": "Advantech WISE 整合現場數據，實現遠端監控、跨據點統一可視化及 AI 智慧優化，協助能源與公用事業提升營運效率與穩定性",
    "challenges": "維運挑戰",
    "why-advantech": "為何選擇 Advantech WISE",
    "digital-journey": "數位維運旅程",
    "use-cases": "應用案例",
    "item-button": "聯絡我們"
  }
}
</i18n>
